import IconCalendarWeekly from '@bimser/icons/16/calendar-weekly';
import IconComparison from '@bimser/icons/16/comparison';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
	BCMobileDateTimePicker,
	BCNotification,
	DatePickerMode,
	IInputChangeEventArgs,
	RangePickerValue,
	findMessage
} from '../';
import Styles from './assets/rangePickerStyles.scss';
import { IMobileRangePickerProps } from './entities';

export default function BCMobileRangePicker(props: IMobileRangePickerProps) {
	const [startDate, setStartDate] = useState<dayjs.Dayjs>(props.value[0]);
	const [endDate, setEndDate] = useState<dayjs.Dayjs>(props.value[1]);

	function onChangeStartDate(date: dayjs.Dayjs) {
		if (endDate && date > endDate) {
			BCNotification.warning({
				message: findMessage.get('100652') + ' !',
				description: findMessage.get('103248')
			});
			return;
		}

		setStartDate(date);
		onChange([date, endDate], [startDate?.format() || '', endDate?.format() || '']);
	}

	function onChangedStartDate(args: IInputChangeEventArgs) {
		if (!args.data) {
			setStartDate(null);
		}
	}

	function onChangeEndDate(date: dayjs.Dayjs) {
		if (date < startDate) {
			BCNotification.warning({
				message: findMessage.get('100652') + ' !',
				description: findMessage.get('103249')
			});
			return;
		}

		setEndDate(date);
		onChange([startDate, date], [startDate?.format() || '', endDate?.format() || '']);
	}

	function onChangedEndDate(args: IInputChangeEventArgs) {
		if (!args.data) {
			setEndDate(null);
		}
	}

	function onChange(dates: RangePickerValue, dateStrings: [string, string]) {
		if (props.onChange) {
			let _startDate = null;
			let _endDate = null;

			if (dates) {
				_startDate = dates[0] ? dates[0].format() : null;
				_endDate = dates[1] ? dates[1].format() : null;
			}

			props.onChange(dates, [_startDate, _endDate], dateStrings);
		}
	}

	function getDisableBefore() {
		if (props.dateLimitType === 'disabled') {
			switch (props.disabledDateType) {
				case 'past':
					return new Date();
				case 'after':
					return undefined;
				case 'before':
					return new Date(props.disabledDates[0]);
				case 'dynamicRange':
					return dayjs()
						.startOf('day')
						.subtract(props.disabledDateRange - 1, 'day')
						.toDate();
				default:
					return undefined;
			}
		}

		switch (props.activeDateType) {
			case 'week':
				return dayjs().startOf('week').toDate();
			case 'month':
				return dayjs().startOf('month').toDate();
			case 'year':
				return dayjs().startOf('year').toDate();
			case 'range':
				return dayjs(new Date(props.disabledDates[0])).startOf('day').toDate();
			default:
				return undefined;
		}
	}

	function getDisableAfter() {
		if (props.dateLimitType === 'disabled') {
			switch (props.disabledDateType) {
				case 'after':
					return new Date(props.disabledDates[0]);
				case 'dynamicRange':
					return dayjs().endOf('day').add(props.disabledDateRange, 'day').toDate();
				case 'before':
				case 'past':
				default:
					return undefined;
			}
		}

		switch (props.activeDateType) {
			case 'week':
				return dayjs().endOf('week').toDate();
			case 'month':
				return dayjs().endOf('month').toDate();
			case 'year':
				return dayjs().endOf('year').toDate();
			case 'range':
				return dayjs(new Date(props.disabledDates[1])).endOf('day').toDate();
			default:
				return undefined;
		}
	}

	return (
		<>
			<div
				className={['ant-picker ant-picker-range ant-picker-middle', Styles.picker].join(
					' '
				)}
			>
				<div className="ant-picker-input ant-picker-input-active">
					<BCMobileDateTimePicker
						value={props.value[0]}
						format={props.format as string}
						disableAfter={getDisableAfter()}
						disableBefore={getDisableBefore()}
						onChange={onChangeStartDate}
						onChangeDate={onChangedStartDate}
						showTime={props.showTime}
						allowClear={props.allowClear}
						readOnly={props.readOnly}
						disabled={props.disabled}
						placeholder={props.placeholder[0]}
						mode={props.mode as DatePickerMode}
						textAlign={props.textAlign}
					/>
				</div>
				<div className="ant-picker-range-separator">
					<span className="ant-picker-separator">
						<span className="anticon anticon-swap-right">{<IconComparison />}</span>
					</span>
				</div>
				<div className="ant-picker-input">
					<BCMobileDateTimePicker
						value={props.value[1]}
						format={props.format as string}
						disableAfter={getDisableAfter()}
						disableBefore={getDisableBefore()}
						onChange={onChangeEndDate}
						onChangeDate={onChangedEndDate}
						showTime={props.showTime}
						allowClear={props.allowClear}
						readOnly={props.readOnly}
						disabled={props.disabled}
						placeholder={props.placeholder[1]}
						mode={props.mode as DatePickerMode}
						textAlign={props.textAlign}
					/>
				</div>
				<span className="ant-picker-suffix">
					<span className="anticon anticon-calendar">{<IconCalendarWeekly />}</span>
				</span>
			</div>
		</>
	);
}
